import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { useAuth } from "../context/AuthContext";
import { downloadApiFile } from "../utils/download";

function getTravelRequestStatusBadgeClass(status) {
  switch (status) {
    case "Approved":
      return "status-badge status-badge-approved";
    case "Under Review":
      return "status-badge status-badge-review";
    case "Rejected":
      return "status-badge status-badge-rejected";
    case "Cancelled":
      return "status-badge status-badge-cancelled";
    case "Draft":
      return "status-badge status-badge-draft";
    case "Pending":
    default:
      return "status-badge status-badge-pending";
  }
}

function TravelRequestsPage() {
  const history = useHistory();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState({
    user_id: ""
  });
  const role = user?.role || "";
  const canFilterByUser = role !== "staff";

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [filters.user_id]);

  const userNameById = useMemo(
    () =>
      users.reduce((accumulator, user) => {
        accumulator[String(user.id)] = [user.firstname, user.lastname].filter(Boolean).join(" ") || user.username;
        return accumulator;
      }, {}),
    [users]
  );

  const currentUserRecord = useMemo(
    () => users.find((candidate) => candidate.uuid === user?.uuid) || null,
    [user?.uuid, users]
  );

  const currentUserId = String(currentUserRecord?.id || user?.id || "");

  const visibleUsers = useMemo(() => {
    if (!users.length) {
      return [];
    }

    if (role === "staff") {
      return users.filter((candidate) => String(candidate.id) === currentUserId);
    }

    if (role === "supervisor") {
      return users.filter(
        (candidate) => String(candidate.id) === currentUserId || String(candidate.supervisor_id || "") === currentUserId
      );
    }

    return users;
  }, [currentUserId, role, users]);

  async function fetchRequests() {
    setLoading(true);
    setError("");

    try {
      const params = {};
      if (filters.user_id) {
        params.user_id = filters.user_id;
      }

      const response = await api.get("/travel-requests", { params });
      setRequests(response.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load travel requests.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsers() {
    try {
      const response = await api.get("/users");
      setUsers(response.data || []);
    } catch (err) {
      // Keep page working even if lookup loading fails.
    }
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;
    setFilters((current) => ({
      ...current,
      [name]: value
    }));
  }

  function handleClearFilters() {
    setFilters({
      user_id: ""
    });
  }

  async function handleDownload() {
    setDownloading(true);

    try {
      await downloadApiFile("/travel-requests/export", {
        params: {
          user_id: filters.user_id || undefined
        },
        fallbackFileName: "out-of-office.csv"
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download out of office requests.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Travel Requests"
        action={
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => history.push("/travel-requests/new")}>
              Add travel request
            </Button>
            <Button variant="outline-primary" onClick={handleDownload} disabled={downloading || loading} title="Download out of office">
              {downloading ? "Downloading..." : "Download"}
            </Button>
            <Button variant="outline-secondary" onClick={handleClearFilters}>
              Clear filters
            </Button>
          </div>
        }
      />

      <Row className="g-4">
        <Col xs={12}>
          <div className="content-card table-card">
            <h5 className="mb-3">Request list</h5>
            <Row className="g-3 mb-3">
              <Col md={6} xl={3}>
                <label className="form-label">Requested By</label>
                {canFilterByUser ? (
                  <Form.Select name="user_id" value={filters.user_id} onChange={handleFilterChange}>
                    <option value="">All visible users</option>
                    {visibleUsers.map((visibleUser) => (
                      <option key={visibleUser.id} value={visibleUser.id}>
                        {[visibleUser.firstname, visibleUser.lastname].filter(Boolean).join(" ") || visibleUser.username}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    value={
                      currentUserRecord
                        ? [currentUserRecord.firstname, currentUserRecord.lastname].filter(Boolean).join(" ") || currentUserRecord.username
                        : "My travel requests"
                    }
                    readOnly
                  />
                )}
              </Col>
            </Row>
            {loading ? <LoadingState label="Loading travel requests..." /> : null}
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {!loading && !error && !requests.length ? (
              <EmptyState title="No travel requests yet" text="Travel requests will appear here once submitted." />
            ) : null}

            {!loading && requests.length ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                        <th className="travel-request-requester-cell">Requested By</th>
                        <th className="travel-request-purpose-cell">Purpose</th>
                        <th className="travel-request-date-cell">Travel Date</th>
                        <th className="travel-request-date-cell">Return Date</th>
                        <th className="travel-request-destination-cell">Destination</th>
                        <th className="travel-request-status-cell">Status</th>
                        <th className="text-end travel-request-action-cell">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.uuid}>
                        <td className="travel-request-requester-cell">{userNameById[String(request.user_id)] || request.user_id}</td>
                        <td className="travel-request-purpose-cell">{request.travel_purpose || "-"}</td>
                        <td className="travel-request-date-cell">{request.travel_from}</td>
                        <td className="travel-request-date-cell">{request.travel_to}</td>
                        <td className="travel-request-destination-cell">{request.destination}</td>
                        <td className="travel-request-status-cell">
                          <span className={getTravelRequestStatusBadgeClass(request.status)}>{request.status}</span>
                        </td>
                        <td className="text-end travel-request-action-cell">
                          <Button variant="outline-secondary" size="sm" onClick={() => history.push(`/travel-requests/${request.uuid}`)}>
                            View details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default TravelRequestsPage;
